<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="cfg.title" :title1="cfg.subtitle" />

        <div v-if="loading" class="pt-card pt-state">
          <div class="pt-spinner"></div>
          <span>Loading {{ cfg.noun }} details...</span>
        </div>

        <div v-else-if="!party" class="pt-card pt-state">
          <strong>{{ cfg.Noun }} not found</strong>
          <span>It may have been deleted, or the link is incorrect.</span>
          <router-link :to="cfg.listPath" class="btn btn-submit mt-2">Back to {{ cfg.nounPlural }}</router-link>
        </div>

        <template v-else>
          <!-- Hero -->
          <section class="pt-card pt-hero">
            <span class="pt-avatar" aria-hidden="true">{{ initials }}</span>
            <div class="pt-hero-body">
              <div class="pt-chips">
                <span class="pt-chip" :class="partyTypeChip.cls">{{ partyTypeChip.label }}</span>
                <span v-if="balanceChip" class="pt-chip" :class="balanceChip.cls">{{ balanceChip.label }}</span>
                <span v-if="kind === 'customer'" class="pt-chip pt-chip-muted">
                  WhatsApp updates {{ party.whatsapp_opt_in === false ? "off" : "on" }}
                </span>
              </div>
              <h2 class="pt-name">{{ partyName }}</h2>
              <p v-if="kind === 'vendor' && party.contactperson" class="pt-sub">Contact: {{ party.contactperson }}</p>
              <p class="pt-sub">
                <template v-if="stats.first_bill_date">{{ cfg.Noun }} since {{ formatDate(stats.first_bill_date) }}</template>
                <template v-else-if="party.createdat">Added {{ formatDate(party.createdat) }}</template>
                <template v-if="stats.last_bill_date"> &middot; Last {{ cfg.billNoun }} {{ relativeDays(stats.last_bill_date) }}</template>
              </p>

              <div class="pt-contacts">
                <a v-if="phone" :href="`tel:${phone}`" class="pt-contact"><i class="fas fa-phone-alt" aria-hidden="true"></i> {{ phone }}</a>
                <a v-if="whatsappLink" :href="whatsappLink" target="_blank" rel="noopener" class="pt-contact"><i class="fab fa-whatsapp" aria-hidden="true"></i> WhatsApp</a>
                <a v-if="party.email" :href="`mailto:${party.email}`" class="pt-contact"><i class="far fa-envelope" aria-hidden="true"></i> {{ party.email }}</a>
                <span v-if="!phone && !party.email" class="pt-contact pt-contact-empty">No phone or email saved</span>
              </div>
            </div>

            <div class="pt-hero-actions">
              <router-link v-if="canCreateBill" :to="cfg.newBillPath" class="btn btn-submit">
                <i class="fas fa-plus me-1" aria-hidden="true"></i> {{ cfg.newBillLabel }}
              </router-link>
              <button v-if="canEdit" type="button" class="btn pt-btn-light" @click="editParty">
                <i class="fas fa-pen me-1" aria-hidden="true"></i> Edit {{ cfg.noun }}
              </button>
              <router-link :to="cfg.listPath" class="btn pt-btn-light">Back to {{ cfg.nounPlural }}</router-link>
            </div>
          </section>

          <div v-if="!canViewActivity" class="pt-alert">
            <i class="fas fa-lock" aria-hidden="true"></i>
            You don't have permission to view {{ cfg.activityNoun }}, so only contact details are shown.
          </div>

          <template v-else>
            <!-- KPIs -->
            <div class="pt-kpis">
              <div class="pt-card pt-kpi">
                <span class="pt-kpi-label">{{ cfg.totalLabel }}</span>
                <strong class="pt-kpi-value">{{ money(cfg.totalValue(stats)) }}</strong>
                <span class="pt-kpi-sub">{{ stats.bill_count || 0 }} {{ (stats.bill_count === 1) ? cfg.billNoun : cfg.billNounPlural }} &middot; avg {{ money(stats.average_bill) }}</span>
              </div>
              <div class="pt-card pt-kpi" :class="{ 'pt-kpi-alert': stats.outstanding > 0 }">
                <span class="pt-kpi-label">{{ cfg.outstandingLabel }}</span>
                <strong class="pt-kpi-value" :class="stats.outstanding > 0 ? 'text-warn' : 'text-good'">{{ money(stats.outstanding) }}</strong>
                <span class="pt-kpi-sub">
                  <template v-if="stats.overdue > 0"><span class="text-bad">{{ money(stats.overdue) }} overdue</span> &middot; </template>
                  {{ stats.unpaid_count || 0 }} unpaid {{ stats.unpaid_count === 1 ? cfg.billNoun : cfg.billNounPlural }}
                </span>
              </div>
              <div class="pt-card pt-kpi">
                <span class="pt-kpi-label">{{ cfg.paidLabel }}</span>
                <strong class="pt-kpi-value">{{ money(stats.total_paid) }}</strong>
                <span class="pt-kpi-sub">{{ paidPercent }}% of billed amount</span>
              </div>
              <div class="pt-card pt-kpi">
                <span class="pt-kpi-label">Returns</span>
                <strong class="pt-kpi-value">{{ money(stats.return_amount) }}</strong>
                <span class="pt-kpi-sub">{{ stats.return_count || 0 }} confirmed {{ stats.return_count === 1 ? "return" : "returns" }}</span>
              </div>
            </div>

            <div class="pt-layout">
              <div class="pt-main">
                <!-- Trend -->
                <section class="pt-card pt-section">
                  <div class="pt-section-head">
                    <div>
                      <h5>{{ cfg.trendTitle }}</h5>
                      <p>Last 12 months &middot; {{ money(trendTotal) }} total</p>
                    </div>
                  </div>
                  <div v-if="!trendTotal" class="pt-empty">No {{ cfg.billNounPlural }} in the last 12 months.</div>
                  <div v-else class="pt-chart" role="img" :aria-label="`${cfg.trendTitle} by month`">
                    <div class="pt-chart-grid">
                      <span v-for="tick in trendTicks" :key="tick" class="pt-chart-tick">
                        <em>{{ compactMoney(tick) }}</em>
                      </span>
                    </div>
                    <div class="pt-chart-bars">
                      <div
                        v-for="point in monthly"
                        :key="point.month"
                        class="pt-chart-col"
                        tabindex="0"
                        @mouseenter="hoverMonth = point.month"
                        @mouseleave="hoverMonth = null"
                        @focus="hoverMonth = point.month"
                        @blur="hoverMonth = null"
                      >
                        <div class="pt-chart-track">
                          <span
                            class="pt-chart-bar"
                            :class="{ dim: hoverMonth && hoverMonth !== point.month }"
                            :style="{ height: `${barHeight(point.amount)}%` }"
                          ></span>
                        </div>
                        <span class="pt-chart-label">{{ monthLabel(point.month) }}</span>
                        <div v-if="hoverMonth === point.month" class="pt-tooltip" role="tooltip">
                          <strong>{{ monthLabel(point.month, true) }}</strong>
                          <span>{{ money(point.amount) }}</span>
                          <small>{{ point.bills }} {{ point.bills === 1 ? cfg.billNoun : cfg.billNounPlural }}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Activity tabs -->
                <section class="pt-card pt-section">
                  <div class="pt-tabs" role="tablist">
                    <button
                      v-for="tab in tabs"
                      :key="tab.key"
                      type="button"
                      role="tab"
                      class="pt-tab"
                      :class="{ active: activeTab === tab.key }"
                      :aria-selected="activeTab === tab.key"
                      @click="activeTab = tab.key"
                    >
                      {{ tab.label }}
                      <span v-if="tab.count" class="pt-tab-count" :class="{ warn: tab.warn }">{{ tab.count }}</span>
                    </button>
                  </div>

                  <div v-if="insightsLoading" class="pt-empty">Loading activity...</div>

                  <!-- Recent bills -->
                  <template v-else-if="activeTab === 'bills'">
                    <div v-if="!insights.recent_bills.length" class="pt-empty">No {{ cfg.billNounPlural }} yet.</div>
                    <div v-else class="table-responsive">
                      <table class="pt-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Bill No.</th>
                            <th class="num">Items</th>
                            <th class="num">Amount</th>
                            <th class="num">Balance</th>
                            <th>Payment</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="bill in insights.recent_bills" :key="bill[cfg.billIdField]">
                            <td>{{ formatDate(bill.billdate) }}</td>
                            <td>
                              <router-link :to="billLink(bill)" class="pt-link">{{ bill.billno || "View" }}</router-link>
                              <small v-if="bill.invoiceno" class="pt-subtext">Inv {{ bill.invoiceno }}</small>
                            </td>
                            <td class="num">{{ bill.itemcount }}</td>
                            <td class="num strong">{{ money(bill.netamount) }}</td>
                            <td class="num" :class="{ 'text-warn': Number(bill.balance) > 0 }">{{ Number(bill.balance) > 0 ? money(bill.balance) : "-" }}</td>
                            <td>
                              <span class="pt-pill" :class="paymentPill(bill).cls">{{ paymentPill(bill).label }}</span>
                              <small v-if="bill.paymentmode" class="pt-subtext">{{ titleCase(bill.paymentmode) }}</small>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <router-link v-if="stats.bill_count > insights.recent_bills.length" :to="cfg.billListPath" class="pt-more">
                        Showing latest {{ insights.recent_bills.length }} of {{ stats.bill_count }} &middot; Open {{ cfg.billNoun }} list &rsaquo;
                      </router-link>
                    </div>
                  </template>

                  <!-- Unpaid -->
                  <template v-else-if="activeTab === 'unpaid'">
                    <div v-if="!insights.unpaid_bills.length" class="pt-empty pt-empty-good">
                      <i class="fas fa-check-circle" aria-hidden="true"></i> All {{ cfg.billNounPlural }} are fully paid.
                    </div>
                    <div v-else class="table-responsive">
                      <table class="pt-table">
                        <thead>
                          <tr>
                            <th>Bill No.</th>
                            <th>Bill Date</th>
                            <th>Due</th>
                            <th class="num">Amount</th>
                            <th class="num">Paid</th>
                            <th class="num">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="bill in insights.unpaid_bills" :key="bill[cfg.billIdField]">
                            <td><router-link :to="billLink(bill)" class="pt-link">{{ bill.billno || "View" }}</router-link></td>
                            <td>{{ formatDate(bill.billdate) }}</td>
                            <td>
                              <template v-if="bill.due_date">
                                {{ formatDate(bill.due_date) }}
                                <span v-if="Number(bill.days_overdue) > 0" class="pt-pill pill-bad">{{ bill.days_overdue }}d overdue</span>
                              </template>
                              <span v-else class="muted">No due date</span>
                            </td>
                            <td class="num">{{ money(bill.netamount) }}</td>
                            <td class="num">{{ money(bill.paid_amount) }}</td>
                            <td class="num strong text-warn">{{ money(bill.balance) }}</td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colspan="5" class="num">Total outstanding</td>
                            <td class="num strong text-warn">{{ money(stats.outstanding) }}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </template>

                  <!-- Payments -->
                  <template v-else-if="activeTab === 'payments'">
                    <p class="pt-note">{{ cfg.paymentsNote }}</p>
                    <div v-if="!insights.payments.length" class="pt-empty">No payments recorded.</div>
                    <div v-else class="table-responsive">
                      <table class="pt-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Receipt No.</th>
                            <th>Against Bill</th>
                            <th>Mode</th>
                            <th class="num">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="payment in insights.payments" :key="payment[cfg.paymentIdField]">
                            <td>{{ formatDate(payment[cfg.paymentDateField]) }}</td>
                            <td>
                              {{ payment.billno }}
                              <small v-if="payment.remarks" class="pt-subtext">{{ payment.remarks }}</small>
                            </td>
                            <td>
                              <router-link v-if="payment.reference_no && payment[cfg.paymentBillField]" :to="{ path: cfg.billDetailPath, query: { id: payment.reference_no } }" class="pt-link">
                                {{ payment[cfg.paymentBillField] }}
                              </router-link>
                              <span v-else class="muted">-</span>
                            </td>
                            <td>{{ titleCase(payment.payment_mode) }}</td>
                            <td class="num strong text-good">{{ money(payment.amount) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </template>

                  <!-- Items -->
                  <template v-else-if="activeTab === 'items'">
                    <div v-if="!insights.top_items.length" class="pt-empty">No items yet.</div>
                    <div v-else class="table-responsive">
                      <table class="pt-table">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th class="num">Qty</th>
                            <th class="num">{{ cfg.itemsAmountLabel }}</th>
                            <th class="num">Bills</th>
                            <th v-if="kind === 'vendor'" class="num">Last Rate</th>
                            <th>Last {{ cfg.billNoun }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in insights.top_items" :key="row.itemid">
                            <td>
                              <router-link v-if="canViewItems" :to="{ path: '/product-details', query: { id: row.itemid } }" class="pt-link">{{ row.itemname }}</router-link>
                              <span v-else>{{ row.itemname }}</span>
                            </td>
                            <td class="num">{{ formatNum(row.quantity) }} <small class="muted">{{ row.unit }}</small></td>
                            <td class="num strong">
                              {{ money(row.amount) }}
                              <span class="pt-share" :style="{ width: `${itemShare(row.amount)}%` }" aria-hidden="true"></span>
                            </td>
                            <td class="num">{{ row.bills }}</td>
                            <td v-if="kind === 'vendor'" class="num">{{ money(row.last_rate) }}</td>
                            <td>{{ formatDate(row.last_bought) }}</td>
                          </tr>
                        </tbody>
                      </table>
                      <p class="pt-note">Top {{ insights.top_items.length }} items by {{ cfg.itemsAmountLabel.toLowerCase() }}.</p>
                    </div>
                  </template>

                  <!-- Returns -->
                  <template v-else-if="activeTab === 'returns'">
                    <div v-if="!insights.returns.length" class="pt-empty">No returns.</div>
                    <div v-else class="table-responsive">
                      <table class="pt-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Return No.</th>
                            <th>Original Bill</th>
                            <th>Status</th>
                            <th class="num">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="ret in insights.returns" :key="ret.returnid">
                            <td>{{ formatDate(ret.returndate) }}</td>
                            <td>
                              <router-link :to="{ path: cfg.returnPath, query: { id: ret.returnid } }" class="pt-link">{{ ret.billno }}</router-link>
                              <small v-if="ret.reason" class="pt-subtext">{{ ret.reason }}</small>
                            </td>
                            <td>{{ ret.originalbillno || "No bill" }}</td>
                            <td><span class="pt-pill" :class="returnPill(ret.status).cls">{{ returnPill(ret.status).label }}</span></td>
                            <td class="num strong">{{ money(ret.netamount) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </template>
                </section>
              </div>

              <!-- Side -->
              <aside class="pt-side">
                <section class="pt-card pt-panel">
                  <h5>Contact & tax details</h5>
                  <dl>
                    <div v-for="row in detailRows" :key="row.label" class="pt-dl-row">
                      <dt>{{ row.label }}</dt>
                      <dd :class="{ muted: !row.value }">{{ row.value || "Not set" }}</dd>
                    </div>
                  </dl>
                </section>

                <section class="pt-card pt-panel">
                  <h5>At a glance</h5>
                  <dl>
                    <div class="pt-dl-row"><dt>First {{ cfg.billNoun }}</dt><dd>{{ formatDate(stats.first_bill_date) }}</dd></div>
                    <div class="pt-dl-row"><dt>Last {{ cfg.billNoun }}</dt><dd>{{ formatDate(stats.last_bill_date) }}</dd></div>
                    <div class="pt-dl-row"><dt>Average {{ cfg.billNoun }}</dt><dd>{{ money(stats.average_bill) }}</dd></div>
                    <div v-if="kind === 'customer'" class="pt-dl-row"><dt>Discount given</dt><dd>{{ money(stats.total_discount) }}</dd></div>
                    <div v-if="kind === 'vendor'" class="pt-dl-row"><dt>Tax paid (input GST)</dt><dd>{{ money(stats.total_tax) }}</dd></div>
                    <div class="pt-dl-row"><dt>Overdue bills</dt><dd :class="{ 'text-bad': stats.overdue_count }">{{ stats.overdue_count || 0 }}</dd></div>
                  </dl>
                </section>

                <section v-if="address" class="pt-card pt-panel">
                  <h5>Address</h5>
                  <p class="pt-address">{{ address }}</p>
                  <a :href="`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`" target="_blank" rel="noopener" class="pt-link">
                    <i class="fas fa-map-marker-alt me-1" aria-hidden="true"></i>Open in Maps
                  </a>
                </section>
              </aside>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const CONFIG = {
  customer: {
    title: "Customer Details",
    subtitle: "Sales, payments and dues of a customer",
    noun: "customer",
    Noun: "Customer",
    nounPlural: "customers",
    endpoint: "/v1/customers",
    idField: "customerid",
    nameField: "customername",
    phoneField: "mobile",
    gstField: "gstno",
    listPath: "/customerlist",
    editPath: "/editcustomer",
    newBillPath: "/add-sales",
    newBillLabel: "New Sale",
    billListPath: "/saleslist",
    billDetailPath: "/sales-details",
    billIdField: "saleid",
    billNoun: "sale",
    billNounPlural: "sales",
    activityNoun: "sales",
    returnPath: "/createsalesreturn",
    paymentIdField: "receiptid",
    paymentDateField: "receipt_date",
    paymentBillField: "salebillno",
    paymentsNote: "Payments received against credit sales. Cash sales paid in full at billing are not listed here.",
    totalLabel: "Total sales",
    totalValue: (stats) => stats.total_sales,
    outstandingLabel: "To receive",
    paidLabel: "Received",
    trendTitle: "Sales trend",
    itemsAmountLabel: "Sales value",
    editPermission: "customer.edit",
    activityPermission: "sales.view",
    createPermission: "sales.add",
  },
  vendor: {
    title: "Supplier Details",
    subtitle: "Purchases, payments and dues of a supplier",
    noun: "supplier",
    Noun: "Supplier",
    nounPlural: "suppliers",
    endpoint: "/v1/vendors",
    idField: "vendorid",
    nameField: "vendorname",
    phoneField: "phone",
    gstField: "gstin",
    listPath: "/supplierlist",
    editPath: "/editsupplier",
    newBillPath: "/addpurchase",
    newBillLabel: "New Purchase",
    billListPath: "/purchaselist",
    billDetailPath: "/purchase-details",
    billIdField: "purchaseid",
    billNoun: "purchase",
    billNounPlural: "purchases",
    activityNoun: "purchases",
    returnPath: "/createpurchasereturn",
    paymentIdField: "paymentid",
    paymentDateField: "payment_date",
    paymentBillField: "purchasebillno",
    paymentsNote: "Payments made against credit purchases. Purchases paid in full at billing are not listed here.",
    totalLabel: "Total purchases",
    totalValue: (stats) => stats.total_purchases,
    outstandingLabel: "To pay",
    paidLabel: "Paid",
    trendTitle: "Purchase trend",
    itemsAmountLabel: "Purchase value",
    editPermission: "supplier.edit",
    activityPermission: "purchase.view",
    createPermission: "purchase.add",
  },
};

const STATE_NAMES = {
  "01": "Jammu & Kashmir", "02": "Himachal Pradesh", "03": "Punjab", "04": "Chandigarh", "05": "Uttarakhand",
  "06": "Haryana", "07": "Delhi", "08": "Rajasthan", "09": "Uttar Pradesh", "10": "Bihar", "11": "Sikkim",
  "12": "Arunachal Pradesh", "13": "Nagaland", "14": "Manipur", "15": "Mizoram", "16": "Tripura", "17": "Meghalaya",
  "18": "Assam", "19": "West Bengal", "20": "Jharkhand", "21": "Odisha", "22": "Chhattisgarh", "23": "Madhya Pradesh",
  "24": "Gujarat", "26": "Dadra & Nagar Haveli and Daman & Diu", "27": "Maharashtra", "29": "Karnataka", "30": "Goa",
  "31": "Lakshadweep", "32": "Kerala", "33": "Tamil Nadu", "34": "Puducherry", "35": "Andaman & Nicobar Islands",
  "36": "Telangana", "37": "Andhra Pradesh", "38": "Ladakh",
};

const emptyInsights = () => ({
  stats: {},
  recent_bills: [],
  unpaid_bills: [],
  payments: [],
  returns: [],
  top_items: [],
  monthly: [],
});

export default {
  name: "PartyDetails",
  props: {
    kind: {
      type: String,
      required: true,
      validator: (value) => ["customer", "vendor"].includes(value),
    },
  },
  data() {
    return {
      party: null,
      loading: false,
      insights: emptyInsights(),
      insightsLoading: false,
      activeTab: "bills",
      hoverMonth: null,
    };
  },
  computed: {
    cfg() {
      return CONFIG[this.kind];
    },
    partyId() {
      return this.$route.query.id || "";
    },
    canEdit() {
      return this.$store.getters.hasPermission(this.cfg.editPermission);
    },
    canViewActivity() {
      return this.$store.getters.hasPermission(this.cfg.activityPermission);
    },
    canCreateBill() {
      return this.$store.getters.hasPermission(this.cfg.createPermission);
    },
    canViewItems() {
      return this.$store.getters.hasPermission("inventory.view");
    },
    stats() {
      return this.insights.stats || {};
    },
    partyName() {
      return this.party?.[this.cfg.nameField] || "-";
    },
    phone() {
      return this.party?.[this.cfg.phoneField] || "";
    },
    gstin() {
      return this.party?.[this.cfg.gstField] || "";
    },
    address() {
      return (this.party?.address || "").trim();
    },
    whatsappLink() {
      const digits = String(this.phone).replace(/\D/g, "");
      if (digits.length < 10) return "";
      return `https://wa.me/${digits.length === 10 ? `91${digits}` : digits}`;
    },
    initials() {
      return String(this.partyName)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join("") || "?";
    },
    partyTypeChip() {
      if (this.gstin) return { label: this.kind === "customer" ? "Business (B2B)" : "GST registered", cls: "pt-chip-info" };
      return { label: this.kind === "customer" ? "Retail (B2C)" : "Unregistered", cls: "pt-chip-muted" };
    },
    balanceChip() {
      if (!this.canViewActivity || this.insightsLoading) return null;
      if (this.stats.overdue > 0) return { label: `${this.money(this.stats.overdue)} overdue`, cls: "pt-chip-bad" };
      if (this.stats.outstanding > 0) return { label: `${this.money(this.stats.outstanding)} due`, cls: "pt-chip-warn" };
      if (this.stats.bill_count) return { label: "No dues", cls: "pt-chip-good" };
      return null;
    },
    paidPercent() {
      const total = Number(this.cfg.totalValue(this.stats)) || 0;
      if (!total) return 0;
      return Math.round((Number(this.stats.total_paid || 0) / total) * 100);
    },
    stateLabel() {
      const code = this.party?.statecode || (this.gstin ? String(this.gstin).slice(0, 2) : "");
      if (!code) return "";
      return STATE_NAMES[code] ? `${STATE_NAMES[code]} (${code})` : code;
    },
    detailRows() {
      const rows = [{ label: this.kind === "customer" ? "Mobile" : "Phone", value: this.phone }];
      if (this.kind === "vendor") {
        rows.push({ label: "Contact person", value: this.party.contactperson });
        rows.push({ label: "Email", value: this.party.email });
      }
      rows.push({ label: this.kind === "customer" ? "GST No." : "GSTIN", value: this.gstin });
      rows.push({ label: "State", value: this.stateLabel });
      if (this.party.createdat) rows.push({ label: "Added on", value: this.formatDate(this.party.createdat) });
      return rows;
    },
    tabs() {
      const unpaid = this.insights.unpaid_bills.length;
      return [
        { key: "bills", label: `Recent ${this.cfg.billNounPlural}`, count: this.stats.bill_count },
        { key: "unpaid", label: "Unpaid bills", count: unpaid, warn: unpaid > 0 },
        { key: "payments", label: "Payments", count: this.insights.payments.length },
        { key: "items", label: this.kind === "customer" ? "Items bought" : "Items supplied", count: this.insights.top_items.length },
        { key: "returns", label: "Returns", count: this.insights.returns.length },
      ];
    },
    monthly() {
      return this.insights.monthly || [];
    },
    trendTotal() {
      return this.monthly.reduce((sum, point) => sum + Number(point.amount || 0), 0);
    },
    trendMax() {
      const max = Math.max(0, ...this.monthly.map((point) => Number(point.amount || 0)));
      if (!max) return 0;
      // Round the axis up to a tidy number so tick labels read cleanly.
      const magnitude = 10 ** Math.floor(Math.log10(max));
      const step = [1, 2, 2.5, 5, 10].find((m) => m * magnitude >= max) * magnitude;
      return step;
    },
    trendTicks() {
      if (!this.trendMax) return [];
      return [this.trendMax, this.trendMax / 2, 0];
    },
    topItemMax() {
      return Math.max(0, ...this.insights.top_items.map((row) => Number(row.amount || 0)));
    },
  },
  watch: {
    "$route.query.id"(newId, oldId) {
      if (newId && newId !== oldId) this.loadAll();
    },
  },
  methods: {
    async loadAll() {
      this.insights = emptyInsights();
      this.activeTab = "bills";
      await this.loadParty();
      if (this.party && this.canViewActivity) this.loadInsights();
    },
    async loadParty() {
      if (!this.partyId) {
        this.party = null;
        return;
      }
      this.loading = true;
      try {
        const response = await axios.get(`${this.cfg.endpoint}/${this.partyId}`);
        this.party = response.data && response.data[this.cfg.idField] ? response.data : null;
      } catch (error) {
        this.party = null;
        if (error.response?.status !== 404) {
          Swal.fire({ icon: "error", title: `Failed to load ${this.cfg.noun}`, text: error.response?.data?.detail || error.message });
        }
      } finally {
        this.loading = false;
      }
    },
    async loadInsights() {
      this.insightsLoading = true;
      try {
        const response = await axios.get(`${this.cfg.endpoint}/${this.partyId}/insights`);
        this.insights = { ...emptyInsights(), ...(response.data || {}) };
        if (this.insights.unpaid_bills.length && this.stats.overdue > 0) {
          this.activeTab = "unpaid";
        }
      } catch (error) {
        this.insights = emptyInsights();
        Swal.fire({ icon: "error", title: "Failed to load activity", text: error.response?.data?.detail || error.message });
      } finally {
        this.insightsLoading = false;
      }
    },
    editParty() {
      this.$router.push({ path: this.cfg.editPath, query: { id: this.partyId } });
    },
    billLink(bill) {
      return { path: this.cfg.billDetailPath, query: { id: bill[this.cfg.billIdField] } };
    },
    paymentPill(bill) {
      const status = String(bill.payment_status || "").toUpperCase();
      if (status === "PAID" || !(Number(bill.balance) > 0)) return { label: "Paid", cls: "pill-good" };
      if (status === "PARTIAL") return { label: "Partly paid", cls: "pill-warn" };
      return { label: "Unpaid", cls: "pill-bad" };
    },
    returnPill(status) {
      const map = {
        CONFIRMED: { label: "Confirmed", cls: "pill-good" },
        DRAFT: { label: "Draft", cls: "pill-warn" },
        CANCELLED: { label: "Cancelled", cls: "pill-muted" },
      };
      return map[status] || { label: status || "-", cls: "pill-muted" };
    },
    barHeight(amount) {
      if (!this.trendMax) return 0;
      const value = Number(amount || 0);
      return value > 0 ? Math.max(2, (value / this.trendMax) * 100) : 0;
    },
    itemShare(amount) {
      if (!this.topItemMax) return 0;
      return Math.round((Number(amount || 0) / this.topItemMax) * 100);
    },
    monthLabel(key, long = false) {
      const [year, month] = String(key).split("-").map(Number);
      const date = new Date(year, month - 1, 1);
      return date.toLocaleDateString("en-IN", long ? { month: "long", year: "numeric" } : { month: "short" });
    },
    relativeDays(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = Math.round((today - date) / 86400000);
      if (days <= 0) return "today";
      if (days === 1) return "yesterday";
      if (days < 30) return `${days} days ago`;
      if (days < 365) return `${Math.round(days / 30)} months ago`;
      return `on ${this.formatDate(value)}`;
    },
    titleCase(value) {
      if (!value) return "-";
      return String(value).toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatNum(value) {
      const number = Number(value || 0);
      return number.toLocaleString("en-IN", { maximumFractionDigits: 2 });
    },
    money(value) {
      return `Rs. ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    },
    compactMoney(value) {
      const number = Number(value || 0);
      if (number >= 10000000) return `${+(number / 10000000).toFixed(1)}Cr`;
      if (number >= 100000) return `${+(number / 100000).toFixed(1)}L`;
      if (number >= 1000) return `${+(number / 1000).toFixed(1)}K`;
      return String(Math.round(number));
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
  },
  beforeMount() {
    this.loadAll();
  },
};
</script>

<style scoped>
.pt-card { background: #fff; border: 1px solid #e9ecf2; border-radius: 12px; }
.pt-state {
  min-height: 220px; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; color: #6b7280; font-size: 14px;
}
.pt-state strong { color: #111827; font-size: 16px; }
.pt-spinner {
  width: 32px; height: 32px; border: 3px solid #f3f4f6; border-top-color: #FF9F43; border-radius: 50%;
  animation: pt-spin 0.7s linear infinite;
}
@keyframes pt-spin { to { transform: rotate(360deg); } }

/* hero */
.pt-hero { display: grid; grid-template-columns: 76px minmax(0, 1fr) auto; gap: 20px; padding: 20px; margin-bottom: 16px; }
.pt-avatar {
  width: 76px; height: 76px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #fff4e8, #ffe2c4); color: #c2410c; font-size: 26px; font-weight: 700;
}
.pt-hero-body { min-width: 0; }
.pt-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 6px; }
.pt-chip { padding: 2px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; background: #f3f4f6; color: #374151; }
.pt-chip-info { background: #e0f2fe; color: #0369a1; }
.pt-chip-muted { background: #f9fafb; color: #6b7280; border: 1px solid #e5e7eb; }
.pt-chip-good { background: #dcfce7; color: #15803d; }
.pt-chip-warn { background: #ffedd5; color: #c2410c; }
.pt-chip-bad { background: #fee2e2; color: #dc2626; }
.pt-name { margin: 0 0 4px; font-size: 24px; font-weight: 700; color: #111827; overflow-wrap: anywhere; }
.pt-sub { margin: 0 0 4px; color: #6b7280; font-size: 13px; }
.pt-contacts { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.pt-contact {
  display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border: 1px solid #e5e7eb; border-radius: 999px;
  color: #374151; font-size: 13px; background: #fff;
}
a.pt-contact:hover { border-color: #FF9F43; color: #c2410c; }
.pt-contact i { color: #9ca3af; }
.pt-contact .fa-whatsapp { color: #16a34a; }
.pt-contact-empty { color: #9ca3af; border-style: dashed; }
.pt-hero-actions { display: flex; flex-direction: column; gap: 8px; }
.pt-hero-actions .btn { white-space: nowrap; min-width: 160px; }
.pt-btn-light { border: 1px solid #e5e7eb; background: #fff; color: #374151; }
.pt-btn-light:hover { border-color: #FF9F43; color: #c2410c; }

.pt-alert {
  display: flex; align-items: center; gap: 10px; padding: 12px 16px; border: 1px solid #e5e7eb; border-radius: 10px;
  background: #f9fafb; color: #4b5563; font-size: 13px;
}

/* kpis */
.pt-kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-bottom: 16px; }
.pt-kpi { padding: 16px 18px; display: flex; flex-direction: column; gap: 6px; }
.pt-kpi-alert { border-color: #fed7aa; background: #fffcf8; }
.pt-kpi-label { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; }
.pt-kpi-value { font-size: 22px; font-weight: 700; color: #111827; line-height: 1.2; }
.pt-kpi-sub { font-size: 12px; color: #6b7280; }
.text-good { color: #15803d !important; }
.text-warn { color: #c2410c !important; }
.text-bad { color: #dc2626 !important; }
.muted { color: #9ca3af !important; }

/* layout */
.pt-layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 16px; align-items: start; }
.pt-main { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.pt-section { padding: 16px 18px 18px; min-width: 0; }
.pt-section-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
.pt-section-head h5 { margin: 0; font-size: 15px; font-weight: 700; color: #111827; }
.pt-section-head p { margin: 2px 0 0; font-size: 12px; color: #6b7280; }

/* chart */
.pt-chart { position: relative; height: 200px; padding-left: 44px; }
.pt-chart-grid { position: absolute; inset: 0 0 22px 44px; display: flex; flex-direction: column; justify-content: space-between; }
.pt-chart-tick { position: relative; border-top: 1px solid #f1f2f4; height: 0; }
.pt-chart-tick em {
  position: absolute; right: calc(100% + 8px); top: -8px; font-style: normal; font-size: 11px; color: #9ca3af;
  font-variant-numeric: tabular-nums;
}
.pt-chart-bars { position: relative; display: flex; align-items: stretch; gap: 2px; height: 100%; }
.pt-chart-col { position: relative; flex: 1; display: flex; flex-direction: column; align-items: center; outline: none; }
.pt-chart-track { flex: 1; width: 100%; display: flex; align-items: flex-end; justify-content: center; }
.pt-chart-bar {
  width: min(60%, 28px); background: #FF9F43; border-radius: 4px 4px 0 0; transition: opacity 0.15s ease, height 0.3s ease;
}
.pt-chart-bar.dim { opacity: 0.35; }
.pt-chart-col:focus-visible .pt-chart-bar { outline: 2px solid #c2410c; outline-offset: 2px; }
.pt-chart-label { height: 22px; line-height: 22px; font-size: 11px; color: #6b7280; }
.pt-tooltip {
  position: absolute; bottom: calc(100% - 12px); left: 50%; transform: translateX(-50%); z-index: 3;
  display: flex; flex-direction: column; gap: 1px; min-width: 120px; padding: 8px 10px; border-radius: 8px;
  background: #111827; color: #fff; font-size: 12px; white-space: nowrap; pointer-events: none;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.18);
}
.pt-tooltip strong { font-weight: 600; }
.pt-tooltip small { color: #d1d5db; }

/* tabs */
.pt-tabs { display: flex; gap: 4px; border-bottom: 1px solid #eef0f4; margin: -6px 0 14px; overflow-x: auto; }
.pt-tab {
  border: 0; background: transparent; padding: 12px 12px 10px; font-size: 14px; font-weight: 600; color: #6b7280;
  border-bottom: 2px solid transparent; margin-bottom: -1px; white-space: nowrap;
}
.pt-tab:hover { color: #111827; }
.pt-tab.active { color: #c2410c; border-bottom-color: #FF9F43; }
.pt-tab-count { margin-left: 6px; padding: 1px 8px; border-radius: 999px; background: #f3f4f6; color: #6b7280; font-size: 11px; }
.pt-tab-count.warn { background: #ffedd5; color: #c2410c; }
.pt-tab.active .pt-tab-count { background: #fff4e8; color: #c2410c; }

.pt-table { width: 100%; border-collapse: collapse; }
.pt-table th, .pt-table td {
  padding: 10px; border-bottom: 1px solid #f1f2f4; font-size: 13px; vertical-align: middle; color: #374151;
}
.pt-table th {
  background: #f9fafb; color: #6b7280; font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.04em; white-space: nowrap;
}
.pt-table tbody tr:hover td { background: #fffaf5; }
.pt-table tfoot td { border-bottom: 0; background: #fafbfc; font-weight: 600; }
.pt-table .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.pt-table .strong { font-weight: 600; color: #111827; }
.pt-subtext { display: block; font-size: 11px; color: #9ca3af; }
.pt-link { color: #c2410c; font-weight: 600; }
.pt-link:hover { text-decoration: underline; }
.pt-pill { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 600; white-space: nowrap; }
.pill-good { background: #dcfce7; color: #15803d; }
.pill-warn { background: #ffedd5; color: #c2410c; }
.pill-bad { background: #fee2e2; color: #dc2626; }
.pill-muted { background: #f3f4f6; color: #6b7280; }
.pt-share { display: block; height: 3px; margin: 4px 0 0 auto; border-radius: 2px; background: #fdba74; }
.pt-empty {
  padding: 28px 16px; text-align: center; color: #6b7280; font-size: 13px; border: 1px dashed #e5e7eb;
  border-radius: 10px; background: #fafafa;
}
.pt-empty-good { color: #15803d; background: #f6fef9; border-color: #bbf7d0; }
.pt-note { margin: 0 0 10px; font-size: 12px; color: #9ca3af; }
.pt-table + .pt-note { margin: 10px 0 0; }
.pt-more { display: inline-block; margin-top: 10px; font-size: 12px; color: #c2410c; font-weight: 600; }

/* side */
.pt-side { display: flex; flex-direction: column; gap: 14px; }
.pt-panel { padding: 16px 18px; }
.pt-panel h5 { margin: 0 0 10px; font-size: 14px; font-weight: 700; color: #111827; }
.pt-panel dl { margin: 0; }
.pt-dl-row { display: flex; justify-content: space-between; gap: 12px; padding: 7px 0; border-bottom: 1px dashed #eef0f4; font-size: 13px; }
.pt-dl-row:last-child { border-bottom: 0; }
.pt-dl-row dt { font-weight: 400; color: #6b7280; }
.pt-dl-row dd { margin: 0; font-weight: 600; color: #111827; text-align: right; overflow-wrap: anywhere; }
.pt-dl-row dd.muted { font-weight: 400; }
.pt-address { margin: 0 0 8px; font-size: 13px; color: #374151; white-space: pre-line; }

@media (max-width: 1200px) {
  .pt-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .pt-layout { grid-template-columns: 1fr; }
  .pt-side { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 768px) {
  .pt-hero { grid-template-columns: 56px minmax(0, 1fr); }
  .pt-avatar { width: 56px; height: 56px; font-size: 20px; }
  .pt-hero-actions { grid-column: 1 / -1; flex-direction: row; flex-wrap: wrap; }
  .pt-hero-actions .btn { flex: 1; min-width: 0; }
  .pt-side { grid-template-columns: 1fr; }
  .pt-chart-label { font-size: 10px; }
}
@media (max-width: 576px) {
  .pt-kpis { grid-template-columns: 1fr; }
  .pt-name { font-size: 20px; }
}
</style>
